import { faceRectToBox } from "@/lib/ai/calibration/ai-calibration-utils"
import { Rect } from "@/lib/ai/detection/face-detection"
import { vec2 } from "gl-matrix"
import { PixelData, toNativeImageData } from "./image-data"
import { isFirefox, isSafari } from "@/utils/browser"

export function fitToSize(sourceSize: vec2, targetSize: vec2): vec2 {
	const [sourceWidth, sourceHeight] = sourceSize
	const [targetWidth, targetHeight] = targetSize

	const scale = Math.min(targetWidth / sourceWidth, targetHeight / sourceHeight)

	return [
		Math.min(targetWidth, Math.ceil(sourceWidth * scale)),
		Math.min(targetHeight, Math.ceil(sourceHeight * scale)),
	]
}

export function fillToSize(sourceSize: vec2, targetSize: vec2): vec2 {
	const [sourceWidth, sourceHeight] = sourceSize
	const [targetWidth, targetHeight] = targetSize

	const scale = Math.max(targetWidth / sourceWidth, targetHeight / sourceHeight)

	// prettier-ignore
	return [
		Math.max(targetWidth, Math.floor(sourceWidth * scale)),
		Math.max(targetHeight, Math.floor(sourceHeight * scale))
	]
}

interface ResizeImageOptions extends ImageBitmapOptions {
	outputType?: "image/jpeg" | "image/png" | "image/webp"
	outputQuality?: number // 0 - 1
}

let offscreenCanvas: OffscreenCanvas | null = null
const clampNumber = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

/**
 * On Firefox and Safari, return a function-local canvas to avoid shared-state races
 * when canvas operations run concurrently (convertToBlob yields to the event loop).
 * On other browsers, reuse a single global canvas for efficiency.
 */
function getOrCreateCanvas(width: number, height: number): OffscreenCanvas {
	if (isFirefox || isSafari) {
		return new OffscreenCanvas(width, height)
	}
	offscreenCanvas = offscreenCanvas ?? new OffscreenCanvas(width, height)
	offscreenCanvas.width = width
	offscreenCanvas.height = height
	return offscreenCanvas
}
const toFiniteNumber = (value: number, fallback: number) =>
	Number.isFinite(value) ? value : fallback

export async function resizeImage(
	image: ImageBitmapSource,
	options: ResizeImageOptions,
): Promise<Blob> {
	const { outputQuality = 0.7, outputType = "image/jpeg", ...bitmapOptions } = options

	const imageBitmap = await createImageBitmap(image, bitmapOptions)

	const localCanvas = getOrCreateCanvas(imageBitmap.width, imageBitmap.height)

	const ctx = localCanvas.getContext("2d")

	if (!ctx) {
		throw new Error("Could not create canvas context")
	}

	ctx.drawImage(imageBitmap, 0, 0)

	const blob = await localCanvas.convertToBlob({
		type: outputType,
		quality: outputQuality,
	})

	imageBitmap.close()

	return blob
}

export async function resizeImageToSize(props: {
	image: Blob | ImageData
	options: ResizeImageOptions
	longestSide: number
	originalSize?: vec2
}): Promise<Blob> {
	const { outputQuality = 0.7, outputType = "image/jpeg", ...bitmapOptions } = props.options
	let originalSize = props.originalSize
	if (!originalSize) {
		const originalBitmap = await createImageBitmap(props.image)
		originalSize = [originalBitmap.width, originalBitmap.height]
		originalBitmap.close()
	}

	if (
		props.image instanceof Blob &&
		originalSize[0] <= props.longestSide &&
		originalSize[1] <= props.longestSide
	) {
		//No need to resize
		return props.image
	}
	const targetSize = fitToSize(originalSize, [props.longestSide, props.longestSide])
	const targetBitmap = await createImageBitmap(props.image, {
		...bitmapOptions,
		resizeWidth: targetSize[0],
		resizeHeight: targetSize[1],
	})

	const localCanvas = getOrCreateCanvas(targetBitmap.width, targetBitmap.height)

	const ctx = localCanvas.getContext("2d")

	if (!ctx) {
		throw new Error("Could not create canvas context")
	}

	ctx.drawImage(targetBitmap, 0, 0)

	const blob = await localCanvas.convertToBlob({
		type: outputType,
		quality: outputQuality,
	})

	targetBitmap.close()

	return blob
}

export async function resizeImageBitmapToBlob(
	imageBitmap: ImageBitmap,
	resizeWidth: number,
	resizeHeight: number,
): Promise<Blob> {
	const localCanvas = getOrCreateCanvas(resizeWidth, resizeHeight)

	const ctx = localCanvas.getContext("2d")

	if (!ctx) {
		throw new Error("Could not create canvas context")
	}

	ctx.drawImage(imageBitmap, 0, 0, resizeWidth, resizeHeight)

	const blob = await localCanvas.convertToBlob({
		type: "image/jpeg",
		quality: 0.7,
	})

	return blob
}

const resultTypes = ["bitmap", "blob"] as const
type ResultType = (typeof resultTypes)[number]

export function resizeImageBitmapToSize(props: {
	imageBitMap: ImageBitmap
	longestSide: number
	result: ResultType[]
	outputType?: "image/jpeg" | "image/png" | "image/webp"
	outputQuality?: number
}) {
	const originalSize: vec2 = [props.imageBitMap.width, props.imageBitMap.height]
	const targetSize = fitToSize(originalSize, [props.longestSide, props.longestSide])
	return resizeImageBitmap(props.imageBitMap, targetSize[0], targetSize[1], props.result, {
		outputType: props.outputType,
		outputQuality: props.outputQuality,
	})
}

// Yuhe: This gives bad results using canvas resizing - it produces pixelated/noisy image, not recommended for color match
export function resizeImageBitmapToBitmap(
	imageBitmap: ImageBitmap,
	resizeWidth: number,
	resizeHeight: number,
	quality: "low" | "medium" | "high" = "low",
) {
	const localCanvas = getOrCreateCanvas(resizeWidth, resizeHeight)

	const ctx = localCanvas.getContext("2d")
	if (ctx && quality !== "low") {
		ctx.imageSmoothingEnabled = true // default is true
		ctx.imageSmoothingQuality = quality // default is low
	}

	if (!ctx) {
		throw new Error("Could not create canvas context")
	}

	ctx.drawImage(imageBitmap, 0, 0, resizeWidth, resizeHeight)

	return localCanvas.transferToImageBitmap()
}

// Yuhe: These following 3 functions give good results using browser's native image resizing
export async function resizeImageDataToBitmap(
	imageData: ImageData | Blob,
	resizeWidth: number,
	resizeHeight: number,
	quality: "low" | "medium" | "high" = "high",
) {
	const imageBitmap = await createImageBitmap(imageData, {
		resizeWidth,
		resizeHeight,
		resizeQuality: quality,
	})
	return imageBitmap
}

export async function resizePixelDataToBitmap(
	pixelData: PixelData,
	resizeWidth: number,
	resizeHeight: number,
	quality: "low" | "medium" | "high" = "high",
) {
	const imageData = toNativeImageData(pixelData)
	return await createImageBitmap(imageData, {
		resizeWidth,
		resizeHeight,
		resizeQuality: quality,
	})
}

export async function resizeImageBitmapToBitmapUsingBlob(
	imageBitmap: ImageBitmap,
	resizeWidth: number,
	resizeHeight: number,
	quality: "low" | "medium" | "high" = "high",
) {
	const localCanvas = getOrCreateCanvas(imageBitmap.width, imageBitmap.height)

	const ctx = localCanvas.getContext("2d")

	if (!ctx) {
		throw new Error("Could not create canvas context")
	}

	ctx.drawImage(imageBitmap, 0, 0, imageBitmap.width, imageBitmap.height)

	const blob = await localCanvas.convertToBlob({
		type: "image/jpeg",
		quality: 0.92,
	})

	return await createImageBitmap(blob, {
		resizeWidth,
		resizeHeight,
		resizeQuality: quality,
	})
}

export async function resizeImageBitmap(
	imageBitmap: ImageBitmap,
	resizeWidth: number,
	resizeHeight: number,
	result: ResultType[],
	options: Pick<ResizeImageOptions, "outputType" | "outputQuality"> = {},
): Promise<{ bitmap: ImageBitmap | undefined; blob: Blob | undefined }> {
	const { outputType = "image/jpeg", outputQuality = 0.7 } = options
	const localCanvas = getOrCreateCanvas(resizeWidth, resizeHeight)

	const ctx = localCanvas.getContext("2d")

	if (!ctx) {
		throw new Error("Could not create canvas context")
	}

	ctx.drawImage(imageBitmap, 0, 0, resizeWidth, resizeHeight)

	let bitmap: ImageBitmap | undefined
	let blob: Blob | undefined

	if (result.includes("blob")) {
		blob = await localCanvas.convertToBlob({
			type: outputType,
			quality: outputQuality,
		})
	}

	if (result.includes("bitmap")) {
		bitmap = localCanvas.transferToImageBitmap()
	}

	return { bitmap, blob }
}

export async function cropBitmap(bitmap: ImageBitmap, rect: Rect) {
	// TODO: @din this SHOULD NOT be here as it has nothing to do with resizing images directly, either swallow this into this file and rename or move cropBitmap to other place.
	const box = faceRectToBox(rect, bitmap.width, bitmap.height)
	const cropWidth = box[2] - box[0]
	const cropHeight = box[3] - box[1]

	return createImageBitmap(bitmap, box[0], box[1], cropWidth, cropHeight)
}

export async function cropImage(
	image: ImageBitmapSource,
	options: ResizeImageOptions,
	rect: Rect,
): Promise<Blob> {
	const { outputQuality = 0.7, outputType = "image/jpeg", ...bitmapOptions } = options

	const imageBitmap = await createImageBitmap(image, bitmapOptions)
	try {
		const bitmapWidth = imageBitmap.width
		const bitmapHeight = imageBitmap.height

		if (bitmapWidth <= 0 || bitmapHeight <= 0) {
			throw new Error("Cannot crop image with zero width or height")
		}

		const normalizedX = toFiniteNumber(rect.x, 0)
		const normalizedY = toFiniteNumber(rect.y, 0)
		const normalizedWidth = toFiniteNumber(rect.width, 1)
		const normalizedHeight = toFiniteNumber(rect.height, 1)

		const unclampedSourceX = Math.floor(normalizedX * bitmapWidth)
		const unclampedSourceY = Math.floor(normalizedY * bitmapHeight)
		const unclampedSourceWidth = Math.round(normalizedWidth * bitmapWidth)
		const unclampedSourceHeight = Math.round(normalizedHeight * bitmapHeight)

		const sourceX = clampNumber(unclampedSourceX, 0, bitmapWidth - 1)
		const sourceY = clampNumber(unclampedSourceY, 0, bitmapHeight - 1)

		const maxSourceWidth = bitmapWidth - sourceX
		const maxSourceHeight = bitmapHeight - sourceY
		const sourceWidth = clampNumber(unclampedSourceWidth, 1, maxSourceWidth)
		const sourceHeight = clampNumber(unclampedSourceHeight, 1, maxSourceHeight)

		const localCanvas = getOrCreateCanvas(sourceWidth, sourceHeight)

		const ctx = localCanvas.getContext("2d")
		if (!ctx) {
			throw new Error("Could not create canvas context")
		}

		ctx.drawImage(
			imageBitmap,
			sourceX,
			sourceY,
			sourceWidth,
			sourceHeight,
			0,
			0,
			sourceWidth,
			sourceHeight,
		)

		return await localCanvas.convertToBlob({
			type: outputType,
			quality: outputQuality,
		})
	} finally {
		imageBitmap.close()
	}
}
